import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Crown, Check, X, Loader2, Mic, Star, Shield, ChevronDown, Zap, Lock } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { trackPageEvent } from "@/lib/pageAnalytics";

const COMPARISON = [
  { label: "All 30 daily challenges + Simulator + SQL Playground", free: true, premium: true },
  { label: "Duel Mode, Leaderboard & Certification Prep (5 tracks)", free: true, premium: true },
  { label: "AI Mock Interviews", free: "3 total", premium: "Unlimited" },
  { label: "Premium badge on your public portfolio", free: false, premium: true },
  { label: "Priority visibility in recruiter search results", free: false, premium: true },
];

const TRUST_BADGES = [
  { icon: Shield, text: "Secure checkout by Stripe" },
  { icon: Zap, text: "Instant activation, no waiting" },
  { icon: Lock, text: "One-time payment — never a subscription" },
];

const FAQS = [
  { q: "Is this a subscription?", a: "No. Premium is a single one-time payment of $9.99 — you own it forever, no recurring charges." },
  { q: "What exactly is unlocked?", a: "Unlimited AI mock interviews (free tier is capped at 3 total), a Premium badge visible on your public portfolio, and priority placement when verified recruiters search the Talent Directory." },
  { q: "Can my whole team get Premium together?", a: "Yes — if you're part of a Team on Cloudivo, your team admin can buy bulk Team Premium seats from the Team page, which is often cheaper per seat." },
];

export default function PremiumPage() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [openFaq, setOpenFaq] = useState(0);

  const startCheckout = async () => {
    setLoading(true);
    trackPageEvent("/premium", "buy_now_click");
    try {
      const { data } = await api.post("/payments/checkout", {
        package_id: "premium", origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start checkout");
      setLoading(false);
    }
  };

  if (user?.is_premium) {
    return (
      <div className="max-w-lg mx-auto px-4 sm:px-6 py-16 text-center" data-testid="premium-already-active">
        <Crown className="w-12 h-12 text-amber-400 mx-auto mb-4" />
        <h1 className="font-heading text-2xl font-semibold text-white">You're already Premium!</h1>
        <p className="text-sm text-slate-400 mt-2">Enjoy unlimited AI mock interviews, your Premium badge, and priority visibility to recruiters.</p>
        <div className="flex items-center justify-center gap-3 mt-6">
          <Link to="/interview" data-testid="premium-active-interview-link" className="bg-amber-400 hover:bg-amber-300 text-background font-semibold text-sm px-5 py-2.5 rounded-lg">Start a Mock Interview</Link>
          <Link to="/profile" data-testid="premium-active-profile-link" className="bg-white/5 hover:bg-white/10 text-white font-semibold text-sm px-5 py-2.5 rounded-lg">View Your Portfolio</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-16" data-testid="premium-page">
      <div className="text-center mb-10">
        <div className="w-16 h-16 rounded-2xl bg-amber-400/10 border border-amber-400/30 flex items-center justify-center mx-auto mb-4">
          <Crown className="w-8 h-8 text-amber-400" />
        </div>
        <h1 className="font-heading text-3xl sm:text-4xl font-semibold text-white">Go Premium</h1>
        <p className="text-sm text-slate-400 mt-2">One-time payment. Unlock the full career acceleration toolkit — forever.</p>
      </div>

      <div className="glass-panel rounded-2xl p-7 border border-amber-400/20 mb-8 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-amber-400/10 to-transparent" />
        <div className="relative">
          <div className="text-center mb-6">
            <span className="font-heading text-4xl font-bold text-white">$9.99</span>
            <span className="text-sm text-slate-400"> one-time</span>
          </div>

          <button onClick={startCheckout} disabled={loading} data-testid="premium-upgrade-btn"
            className="w-full flex items-center justify-center gap-2 bg-amber-400 hover:bg-amber-300 disabled:opacity-60 text-background font-bold text-sm py-3 rounded-xl transition-colors mb-4">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Crown className="w-4 h-4" />}
            {loading ? "Redirecting to checkout..." : "Upgrade Now"}
          </button>

          <div className="flex flex-wrap items-center justify-center gap-4">
            {TRUST_BADGES.map((t, i) => {
              const Icon = t.icon;
              return (
                <span key={i} className="flex items-center gap-1.5 text-[11px] text-slate-400" data-testid={`premium-trust-badge-${i}`}>
                  <Icon className="w-3 h-3 text-emerald-400" /> {t.text}
                </span>
              );
            })}
          </div>
        </div>
      </div>

      <div className="glass-panel rounded-2xl overflow-hidden mb-8" data-testid="premium-comparison-table">
        <div className="grid grid-cols-[1fr,72px,72px] px-5 py-3 border-b border-white/5 text-[11px] font-bold uppercase tracking-wider text-slate-400">
          <span>Feature</span>
          <span className="text-center">Free</span>
          <span className="text-center text-amber-300">Premium</span>
        </div>
        {COMPARISON.map((row, i) => (
          <div key={i} className="grid grid-cols-[1fr,72px,72px] px-5 py-3.5 items-center border-b border-white/5 last:border-0" data-testid={`premium-comparison-row-${i}`}>
            <span className="text-sm text-slate-300 pr-2">{row.label}</span>
            <span className="flex justify-center">
              {typeof row.free === "boolean" ? (row.free ? <Check className="w-4 h-4 text-emerald-400" /> : <X className="w-4 h-4 text-slate-500" />) : <span className="text-xs text-slate-400">{row.free}</span>}
            </span>
            <span className="flex justify-center">
              {typeof row.premium === "boolean" ? (row.premium ? <Check className="w-4 h-4 text-amber-400" /> : <X className="w-4 h-4 text-slate-500" />) : <span className="text-xs font-semibold text-amber-300">{row.premium}</span>}
            </span>
          </div>
        ))}
      </div>

      <div className="mb-4">
        <h2 className="font-heading font-semibold text-white text-sm mb-3 flex items-center gap-2"><Star className="w-4 h-4 text-amber-400" /> Common Questions</h2>
        <div className="space-y-2">
          {FAQS.map((f, i) => (
            <div key={i} className="border border-white/10 rounded-xl overflow-hidden" data-testid={`premium-faq-item-${i}`}>
              <button onClick={() => setOpenFaq(openFaq === i ? -1 : i)} data-testid={`premium-faq-question-${i}`}
                className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left bg-white/[0.02] hover:bg-white/[0.04] transition-colors">
                <span className="text-sm font-medium text-white">{f.q}</span>
                <ChevronDown className={`w-4 h-4 text-slate-400 shrink-0 transition-transform ${openFaq === i ? "rotate-180" : ""}`} />
              </button>
              {openFaq === i && (
                <div className="px-4 py-3 text-sm text-slate-400 leading-relaxed border-t border-white/10" data-testid={`premium-faq-answer-${i}`}>{f.a}</div>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="flex items-center gap-2 justify-center text-[11px] text-slate-500 mt-2">
        <Mic className="w-3 h-3" /> Have a Team on Cloudivo? Your admin can buy bulk seats from the <Link to="/team" className="text-indigo-400 hover:text-indigo-300 font-medium">Team page</Link> instead.
      </div>
    </div>
  );
}
